import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { companyDetail, updateMyEligibility } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, CheckCircle2, XCircle, ExternalLink, ShieldCheck, GraduationCap, Loader2, CalendarClock, Brain, Sparkles, HelpCircle } from "lucide-react";

/* One company at your campus: can you apply to its drive, and how to prepare
   for its exact process. Facts from the placement cell's drive + our library;
   the prep content is AI-written around those facts. */

const TABS = [["overview", "Process"], ["eligibility", "Can I apply?"], ["pattern", "Test pattern"], ["prep", "Prep plan"], ["questions", "Interview questions"]];
const STATUS = { pass: "text-emerald-700", fail: "text-red-700", unknown: "text-amber-700" };

export default function CompanyHubPage() {
  const { slug } = useParams();
  const { refresh } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");
  const load = () => companyDetail(slug).then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load this company."));
  useEffect(() => { setD(null); setErr(null); load(); }, [slug]); // eslint-disable-line

  if (err) return <div className="container-x py-16"><p className="text-stone-700 max-w-md">{err}</p><Link to="/app/companies" className="text-sm underline mt-3 inline-block">← Companies at your campus</Link></div>;
  if (!d) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Preparing the hub…</div>;

  const { profile: p, drives, pattern, hub } = d;
  const ok = drives.some((x) => x.eligible);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : null);
  const mockDrive = drives.find((x) => x.eligible);

  return (
    <div className="container-x py-10 md:py-14" data-testid="company-hub">
      <Link to="/app/companies" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900"><ArrowLeft size={14} /> Companies at your campus</Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl bg-stone-900 text-amber-300 font-display text-2xl flex items-center justify-center">{p.name[0]}</span>
          <div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">{p.name}</h1>
            <p className="text-sm text-stone-500 mt-1">{p.category}{p.official_url && <> · <a href={p.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-[#C2410C]">official careers page <ExternalLink size={11} /></a></>}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`} data-testid="hub-verdict">
          {ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />} {ok ? "You can apply" : "Not eligible for this drive"}
        </span>
      </div>

      {/* The drive(s) */}
      <div className="mt-6 rounded-3xl border-2 border-[#C2410C]/30 bg-amber-50/60 p-5" data-testid="hub-drive">
        <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">At your campus</p>
        <div className="mt-2 space-y-2">
          {drives.map((dr) => (
            <div key={dr.drive_id} className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-display text-xl">{dr.title}{dr.role ? <span className="text-stone-500 text-base"> · {dr.role}</span> : null}
                {dr.deadline && <span className="ml-3 text-xs text-stone-600 inline-flex items-center gap-1"><CalendarClock size={12} /> apply by {fmt(dr.deadline)}</span>}
                {dr.applied && <span className="ml-3 text-xs text-emerald-700 font-medium">Applied ✓</span>}</p>
              <span className={`text-xs font-medium ${dr.eligible ? "text-emerald-700" : "text-red-700"}`}>{dr.eligible ? "eligible" : "not eligible"}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {pattern && mockDrive && <Link to={`/app/company-tests?drive=${mockDrive.drive_id}&company=${encodeURIComponent(p.name)}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-semibold hover:bg-[#9a3412]"><ShieldCheck size={14} /> Take the {p.name} mock</Link>}
          <Link to="/app/campus" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-medium hover:border-stone-900"><GraduationCap size={14} /> Open drive & apply</Link>
        </div>
      </div>

      <div className="mt-8 flex gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`hub-tab-${k}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${tab === k ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:bg-stone-100"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && <Overview p={p} hub={hub} />}
        {tab === "eligibility" && <Eligibility p={p} drives={drives} general={d.general_eligibility} missing={d.profile_missing} onSaved={async () => { await refresh?.(); load(); }} />}
        {tab === "pattern" && <Pattern p={p} pattern={pattern} mockDrive={mockDrive} hub={hub} />}
        {tab === "prep" && <Prep hub={hub} />}
        {tab === "questions" && <Questions hub={hub} name={p.name} />}
      </div>
    </div>
  );
}

const Card = ({ children, className = "" }) => <div className={`rounded-3xl border border-stone-200 bg-white p-6 md:p-8 ${className}`}>{children}</div>;
const H = ({ children }) => <h2 className="font-display text-2xl mb-3">{children}</h2>;

function Overview({ p, hub }) {
  const rounds = hub.rounds?.length ? hub.rounds : (p.rounds || []).map((r) => ({ name: r.name, what_happens: r.note, how_to_prepare: "" }));
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2">
        <H>How {p.name} hires freshers</H>
        <p className="text-stone-700 leading-relaxed">{hub.overview}</p>
        <div className="mt-6 space-y-3">
          {rounds.map((r, i) => (
            <div key={i} className="flex gap-4 rounded-2xl bg-[#F5F3ED] p-4">
              <span className="font-display text-2xl text-[#C2410C] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="font-medium">{r.name}</p>
                {r.what_happens && <p className="text-sm text-stone-600 mt-1">{r.what_happens}</p>}
                {r.how_to_prepare && <p className="text-sm text-stone-800 mt-1.5"><b>Prepare:</b> {r.how_to_prepare}</p>}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <H>Quick tips</H>
        <ul className="space-y-2 text-sm text-stone-700">{(hub.tips || []).map((t, i) => <li key={i} className="flex gap-2"><Sparkles size={14} className="text-[#C2410C] mt-0.5 shrink-0" />{t}</li>)}</ul>
        {p.verify_note && <p className="mt-5 text-[11px] text-stone-400">{p.verify_note}</p>}
      </Card>
    </div>
  );
}

function Eligibility({ p, drives, general, missing, onSaved }) {
  const [f, setF] = useState({ pct_10: "", pct_12: "", cgpa: "", active_backlogs: "", education_gap_years: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const save = async () => {
    const patch = {};
    for (const [k, v] of Object.entries(f)) if (v !== "") patch[k] = k === "active_backlogs" ? parseInt(v, 10) : parseFloat(v);
    if (!Object.keys(patch).length) { setMsg("Fill at least one field."); return; }
    setSaving(true); setMsg("");
    try { await updateMyEligibility(patch); setMsg("Saved — re-checked."); onSaved?.(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };
  const ruleLine = (r) => [r.min_cgpa != null && `CGPA ≥ ${r.min_cgpa}`, r.min_wishire_score != null && `Wishire Score ≥ ${r.min_wishire_score}`,
    r.branches?.length && `Branches: ${r.branches.join(", ")}`, r.grad_years?.length && `Batch: ${r.grad_years.join(", ")}`].filter(Boolean);
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <Card>
          <H>This drive's rules — the ones that decide your application</H>
          <div className="space-y-4">
            {drives.map((dr) => (
              <div key={dr.drive_id} className={`rounded-2xl border p-4 ${dr.eligible ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium">{dr.title}{dr.role ? <span className="text-stone-500"> · {dr.role}</span> : null}</p>
                  <span className={`text-sm font-medium inline-flex items-center gap-1 ${dr.eligible ? "text-emerald-700" : "text-red-700"}`}>{dr.eligible ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {dr.eligible ? "You can apply" : "Not eligible"}</span>
                </div>
                <p className="text-xs text-stone-600 mt-1">{ruleLine(dr.rules).join(" · ") || "No restrictions set by your placement cell — everyone in the batch can apply."}</p>
                {!dr.eligible && dr.reasons.length > 0 && <ul className="mt-2 space-y-1">{dr.reasons.map((r, i) => <li key={i} className="text-sm text-red-700 flex gap-2"><XCircle size={13} className="mt-0.5 shrink-0" />{r}</li>)}</ul>}
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-stone-500 flex items-start gap-1.5"><HelpCircle size={13} className="mt-0.5 shrink-0" /> Set by your placement cell for this drive — if something looks wrong (e.g. your CGPA), fix it on your <Link to="/app/profile" className="underline">profile</Link> or ask the cell.</p>
        </Card>

        {general && (
          <Card>
            <H>{p.name}'s published criteria</H>
            <p className="text-xs text-stone-500 mb-3">The company's general hiring criteria, for awareness. Your drive's rules above are what actually apply at your campus.</p>
            <div className="divide-y divide-stone-100">
              {general.checks.map((c, i) => (
                <div key={i} className="py-2.5 grid grid-cols-[1fr_auto_auto] gap-4 text-sm items-center">
                  <span className="text-stone-800">{c.rule}</span>
                  <span className="text-stone-500 font-mono text-xs">{c.required}</span>
                  <span className={`font-medium text-xs ${STATUS[c.status]}`}>{c.status === "pass" ? "✓ " : c.status === "fail" ? "✗ " : "? "}{c.yours}</span>
                </div>
              ))}
            </div>
            {general.notes && <p className="mt-3 text-sm text-stone-600 bg-[#F5F3ED] rounded-xl px-4 py-3">{general.notes}</p>}
            <p className="mt-3 text-[11px] text-stone-400">{p.verify_note} {p.official_url && <a href={p.official_url} target="_blank" rel="noreferrer" className="underline">Official page ↗</a>}</p>
          </Card>
        )}
      </div>

      <Card>
        <H>Your academic details</H>
        <p className="text-xs text-stone-500 mb-3">Used for company criteria checks. Fill once.{missing?.length ? ` Missing: ${missing.join(", ")}.` : ""}</p>
        <div className="space-y-2">
          {[["pct_10", "10th %"], ["pct_12", "12th %"], ["cgpa", "CGPA (out of 10)"], ["active_backlogs", "Active backlogs"], ["education_gap_years", "Education gap (years)"]].map(([k, l]) => (
            <label key={k} className="block text-xs text-stone-600">{l}
              <input type="number" step="any" value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} data-testid={`elig-${k}`}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm focus:outline-none focus:border-stone-900" />
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="elig-save" className="mt-4 w-full px-4 py-2.5 rounded-full bg-stone-900 text-stone-50 text-sm font-medium hover:bg-[#C2410C] disabled:opacity-60">{saving ? "Saving…" : "Save & re-check"}</button>
        {msg && <p className="mt-2 text-xs text-stone-600">{msg}</p>}
      </Card>
    </div>
  );
}

function Pattern({ p, pattern, mockDrive, hub }) {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card>
        <H>Test pattern</H>
        {pattern ? (
          <>
            <p className="font-medium">{pattern.name}</p>
            <div className="mt-3 space-y-1.5">
              {pattern.sections.map((s, i) => <div key={i} className="flex justify-between text-sm"><span className="text-stone-700">{s.label}</span><span className="font-mono text-xs text-stone-400">{s.questions}Q · {s.minutes}m</span></div>)}
            </div>
            <p className="mt-3 text-xs text-stone-500 leading-relaxed">{pattern.real_pattern}</p>
            {mockDrive ? (
              <Link to={`/app/company-tests?drive=${mockDrive.drive_id}&company=${encodeURIComponent(p.name)}`} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-stone-50 text-sm font-medium hover:bg-[#C2410C]"><ShieldCheck size={14} /> Take the proctored {p.name} mock</Link>
            ) : (
              <p className="mt-5 text-xs text-stone-500 bg-[#F5F3ED] rounded-xl px-4 py-3">The proctored mock unlocks once you're eligible for this drive.</p>
            )}
          </>
        ) : (
          <p className="text-sm text-stone-600">We don't hold a verified section-wise pattern for {p.name}. Prepare from the syllabus here and the rounds on the Process tab; the drive assessment your placement cell sets will follow their own structure.</p>
        )}
      </Card>
      <Card>
        <H>Syllabus</H>
        <div className="space-y-3">
          {(hub.syllabus || []).map((s, i) => (
            <div key={i}><p className="text-sm font-medium">{s.area}</p><p className="text-sm text-stone-600">{(s.topics || []).join(" · ")}</p></div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Prep({ hub }) {
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2">
        <H>4-week plan</H>
        <div className="grid sm:grid-cols-2 gap-3">
          {(hub.prep_plan || []).map((w, i) => (
            <div key={i} className="rounded-2xl bg-[#F5F3ED] p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">Week {w.week}</p>
              <p className="font-medium mt-1">{w.focus}</p>
              <ul className="mt-2 space-y-1 text-sm text-stone-700">{(w.tasks || []).map((t, j) => <li key={j} className="flex gap-2"><span className="text-stone-400">•</span>{t}</li>)}</ul>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <H>Common mistakes</H>
        <ul className="space-y-2 text-sm text-stone-700">{(hub.common_mistakes || []).map((m, i) => <li key={i} className="flex gap-2"><XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />{m}</li>)}</ul>
        <Link to="/app/coach" className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-stone-900 hover:text-[#C2410C]"><Brain size={15} /> Ask the coach about this company →</Link>
      </Card>
    </div>
  );
}

function Questions({ hub, name }) {
  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-5">
        {(hub.technical_questions || []).map((t, i) => (
          <Card key={i}>
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">Technical · {t.topic}</p>
            <ol className="mt-3 space-y-1.5 text-sm text-stone-800 list-decimal pl-5">{(t.questions || []).map((q, j) => <li key={j}>{q}</li>)}</ol>
            {t.how_to_approach && <p className="mt-3 text-sm text-stone-600 bg-[#F5F3ED] rounded-xl px-4 py-3"><b>How to approach:</b> {t.how_to_approach}</p>}
          </Card>
        ))}
      </div>
      <Card>
        <H>HR round</H>
        <div className="grid md:grid-cols-2 gap-3">
          {(hub.hr_questions || []).map((q, i) => (
            <div key={i} className="rounded-2xl border border-stone-100 p-4"><p className="text-sm font-medium">{q.question}</p><p className="text-xs text-stone-600 mt-1.5">{q.how_to_approach}</p></div>
          ))}
        </div>
        <p className="mt-4 text-xs text-stone-500">Practise these out loud: <Link to="/app/interview" className="underline hover:text-[#C2410C]">start an AI mock interview</Link> and mention you're preparing for {name}.</p>
      </Card>
    </div>
  );
}
